'use client';

import React, { ReactElement, useEffect, useState } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { UiTooltipPosition, UiTooltipTheme } from '@nx-frontend/shared/types';

const tooltipPositionClasses = {
  [UiTooltipPosition.RIGHT]: 'top-[50%] translate-y-[-50%] right-[-10px] translate-x-[100%]',
  [UiTooltipPosition.BOTTOM]: 'left-[50%] translate-x-[-50%] bottom-[-10px] translate-y-[100%]',
  [UiTooltipPosition.TOP]: 'left-[50%] translate-x-[-50%] top-[-10px] translate-y-[-100%]',
  [UiTooltipPosition.LEFT]: 'top-[50%] translate-y-[-50%] left-[-10px] translate-x-[-100%]',
  [UiTooltipPosition.TOP_LEFT]: 'top-[-50%] translate-y-[-50%] left-[20px] translate-x-[-100%]',
  [UiTooltipPosition.TOP_RIGHT]: 'top-[-50%] translate-y-[-50%] right-[-10px] translate-x-[100%]',
  [UiTooltipPosition.BOTTOM_LEFT]: 'top-[50%] translate-x-[-50%] translate-y-[100%]',
  [UiTooltipPosition.BOTTOM_RIGHT]: 'top-[50%] translate-x-[100%] translate-y-[100%]',
  [UiTooltipPosition.TOP_LEFT_SM]: 'left-[80%] translate-x-[-80%] top-[-10px] translate-y-[-100%]',
};

interface TooltipProps {
  children: ReactElement;
  message: string | undefined | ReactElement;
  additionalMessage?: string | null | ReactElement;
  position?: UiTooltipPosition;
  show?: boolean;
  hideOnMod?: boolean;
  theme?: UiTooltipTheme;
  messageClasses?: string;
  additionalMessageClasses?: string;
  display?: string;
  tooltipLink?: { linkText: string; linkUrl: string };
}

export const Tooltip: React.FC<TooltipProps> = ({
  message,
  messageClasses,
  children,
  hideOnMod = false,
  additionalMessage,
  additionalMessageClasses,
  position = UiTooltipPosition.RIGHT,
  show = true,
  theme = UiTooltipTheme.DARK,
  display = 'flex',
  tooltipLink,
  ...props
}) => {
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  useEffect(() => {
    if (!show) {
      setTransitionEnabled(false);
    } else {
      setTimeout(() => setTransitionEnabled(true), 50);
    }
  }, [show]);

  if (!show) {
    return children;
  }

  const isDarkTheme = theme === UiTooltipTheme.DARK;
  let additionalMessageToShow: ReactElement | null = null;
  if (additionalMessage) {
    additionalMessageToShow = (
      <span className={classNames('mt-1 text-neutral-400', additionalMessageClasses)}>{additionalMessage}</span>
    );
  }

  const tooltipClasses = classNames(
    'flex-col',
    'font-bold',
    'items-start',
    'text-xs',
    'absolute z-[1000] opacity-0 invisible [.group-tooltip:hover_&]:visible',
    'rounded shadow-md [.group-tooltip:hover_&]:opacity-100 p-2 shadow-black-300 scale-100 w-max',

    tooltipPositionClasses[position],
    {
      'flex': !hideOnMod,
      'hidden sm:flex': hideOnMod,
      'bg-neutral-800 text-white border': isDarkTheme,
      'bg-white text-black': !isDarkTheme,
    },
    {
      'transition-all delay-1000 duration-300': transitionEnabled,
    },
    messageClasses
  );
  if (!show) {
    return children; // Return the children as is if show is false
  }
  return (
    <div className={`group-tooltip relative ${display}`} {...props}>
      {children}
      <div className={tooltipClasses} data-cy="tooltip-check">
        {tooltipLink ? (
          <div className="flex flex-row justify-center">
            <div>
              {message}
              {additionalMessageToShow}
            </div>
            <Link href={tooltipLink.linkUrl} target="_blank" className=" text-teal-500 hover:text-teal-400 p-4">
              {tooltipLink.linkText}
            </Link>
          </div>
        ) : (
          <>
            {message}
            {additionalMessageToShow}
          </>
        )}
      </div>
    </div>
  );
};
